import { TABLE_STATUS } from '../../TableStatus';
import React, { useEffect, useMemo, useState } from 'react';
import { ConnectedWallet, Contract, Contract_Constructor, KindOfKit, Kit } from '../../../../../assets/constant/kit.type';
import { logic } from '../../../../../../logic';
import useLoading from '../../../../../../hooks/useLoading';
import { networkParserNew } from '../../../../../../utils/Utils';
import toast from 'react-hot-toast';
import SelectNetworkModal from '../../SelectNetworkModal';
import ConnectWalletModal from '../../ConnectWalletModal';
import EnterPinCodeModal from '../../EnterPinCodeModal';
import KitContainer from '../../KitContainer';
import LabelValue from '../../LabelValue';
import CopyToClipboard from 'react-copy-to-clipboard/lib';
import BasicButton from '../../../../../components/_atom/button/BasicButton';
import { ButtonSize } from '../../../../../components/_atom/button/StyledButton';
import { ReactComponent as IconCopy } from '../../../../../assets/images/icon_copy.svg';

type ContractManagerProps = {
  kindOfKit: KindOfKit;
  kitDetail: Kit;
  setKitDetail: any;
  setTransactionHash: any;
  connectedWallet: ConnectedWallet;
  setConnectedWallet: any;
};
const ContractManager = (props: ContractManagerProps) => {
  const { kindOfKit, kitDetail, setKitDetail, setTransactionHash, connectedWallet, setConnectedWallet } = props;
  const kitId = kitDetail?.kit_id || kitDetail?.nft_kit_id;

  const status =
    kitDetail?.status === 4
      ? TABLE_STATUS.DEPLOYED
      : kitDetail?.status === 3
        ? TABLE_STATUS.PROCESSING
        : kitDetail?.status === 2
          ? TABLE_STATUS.PREPARED
          : TABLE_STATUS.CREATED;
  const [contractInfo, setContractInfo] = useState<Contract>();
  const contractLibrary = useMemo(() => contractInfo?.contractLibrary, [contractInfo]);
  const [constructor, setConstructor] = useState<Contract_Constructor>();
  const getContractInfo = async () => {
    const { result, error } = await logic.contract.getContractDetail(kitDetail?.contract_id);
    if (error) {
      return;
    }
    setContractInfo(result);
  };
  const { loading: getContractInfoLoading } = useLoading({
    dependency: [kitDetail?.contract_id],
    synchronousFunction: async () => {
      await getContractInfo();
    },
  });
  const getContractConstructor = async (contractId: number) => {
    const { result, error } = await logic.contract.getContractConstructor(contractId);
    if (error) {
      return;
    }
    setConstructor(result as Contract_Constructor);
  };
  useEffect(() => {
    if (contractInfo) {
      getContractConstructor(contractInfo.contract_id);
    }
  }, [contractInfo]);

  const [isOpenSelectNetworkModal, setIsOpenSelectNetworkModal] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState<any>();

  const submitKit = async () => {
    if (!kitId) {
      return;
    }
    const makeContractInfo = () => {
      switch (kindOfKit) {
        case KindOfKit.TokenKit:
          return logic.tokenKit.makeContractInfo;
        case KindOfKit.NFTKit:
          return logic.nftKit.makeContractInfo;
        default:
          return logic.tokenKit.makeContractInfo;
      }
    };

    const { result, error } = await makeContractInfo()(kitId, selectedNetwork?.id);
    if (error) {
      return;
    }
    const { result: microChains, error: microChainsError } = await logic.microChain.getMicroChainList({});
    if (microChainsError) {
      return;
    }
    const networks = networkParserNew(microChains);
    const network = networks.find((network) => network.id === result.chain_id);
    const submittedKitDetail: Kit = {
      ...result,
      network: network?.label,
    };
    setKitDetail(submittedKitDetail);
    return true;
  };

  const [password, setPassword] = useState<string>('');
  const [isEnterPinCodeModalOpen, setIsEnterPinCodeModalOpen] = useState(false);
  const changeKitStatus = async () => {
    if (!kitId) {
      return;
    }
    const changeKitStatusProcessing = () => {
      switch (kindOfKit) {
        case KindOfKit.TokenKit:
          return logic.tokenKit.changeTokenKitStatusProcessing;
        case KindOfKit.NFTKit:
          return logic.nftKit.changeNFTKitStatusProcessing;
        default:
          return logic.tokenKit.changeTokenKitStatusProcessing;
      }
    };
    const { result, error } = await changeKitStatusProcessing()(kitId);
    if (error) {
      return;
    }
    setKitDetail((prev) => ({ ...prev, ...result }));
  };
  const deployKitWithMetamask = async () => {
    toast.loading('Deploying kit...');
    if (!kitId) {
      return;
    }
    const deployTransactionWithMetamask = () => {
      switch (kindOfKit) {
        case KindOfKit.TokenKit:
          return logic.tokenKit.tokenKitDeployTransactionWithMetamask;
        case KindOfKit.NFTKit:
          return logic.nftKit.nftKitDeployTransactionWithMetamask;
        default:
          return logic.tokenKit.tokenKitDeployTransactionWithMetamask;
      }
    };
    const { result: tx_hash, error } = await deployTransactionWithMetamask()(kitId);
    if (error) {
      toast.error('Failed to deploy kit');
      return;
    }
    setTransactionHash(tx_hash);
    await changeKitStatus();
    toast.success('Deployed kit successfully!');
  };
  const deployKitWithAccount = async () => {
    if (!kitId) {
      return;
    }
    toast.loading('Deploying kit...');
    if (kindOfKit === KindOfKit.TokenKit) {
      const { result, error } = await logic.tokenKit.tokenKitDeployWithServer(kitId, password);
      if (error) {
        toast.error('Failed to deploy kit');
        return;
      }
      const { transaction_hash } = result || {};
      if (transaction_hash) {
        setTransactionHash(transaction_hash);
      }
      setKitDetail((prev) => ({ ...prev, ...result }));
    }
    if (kindOfKit === KindOfKit.NFTKit) {
      const { result, error } = await logic.nftKit.nftKitDeployWithServer(kitId, password);
      if (error) {
        toast.error('Failed to deploy kit');
        return;
      }
      const { transaction_hash } = result || {};
      if (transaction_hash) {
        setTransactionHash(transaction_hash);
      }
      setKitDetail((prev) => ({ ...prev, ...result }));
    }
    toast.success('Deployed kit successfully!');
  };
  const [isOpenConnectWalletModal, setIsOpenConnectWalletModal] = useState(false);
  const deployKit = async () => {
    if (!connectedWallet.connectAddress) {
      setIsOpenConnectWalletModal(true);
      return;
    }
    if (connectedWallet.connectWalletType === 'metamask') {
      await deployKitWithMetamask();
    }
    if (connectedWallet.connectWalletType === 'accountManager') {
      if (!password || password.length !== 6) {
        setIsEnterPinCodeModalOpen(true);
        return;
      }
      await deployKitWithAccount();
    }
  };

  const [deployLoading, setDeployLoading] = useState(false);
  const onClickDeploy = async () => {
    setDeployLoading(true);
    if (status === TABLE_STATUS.CREATED) {
      if (!selectedNetwork) {
        setIsOpenSelectNetworkModal(true);
        return;
      }
      const submitResult = await submitKit();
      if (submitResult) {
        await deployKit();
      }
    }
    if (status === TABLE_STATUS.PREPARED) {
      await deployKit();
    }
  };

  return (
    <div className="tab-content-wrapper">
      <SelectNetworkModal
        kindOfKit={kindOfKit}
        selectedNetwork={selectedNetwork}
        setSelectedNetwork={setSelectedNetwork}
        kitDetail={kitDetail}
        isOpen={isOpenSelectNetworkModal}
        closeModal={() => setIsOpenSelectNetworkModal(false)}
      />
      <ConnectWalletModal
        isOpen={isOpenConnectWalletModal}
        closeModal={async (connectedWallet) => {
          setIsOpenConnectWalletModal(false);
          if (connectedWallet?.connectWalletType === 'metamask') {
            await deployKitWithMetamask();
          }
          if (connectedWallet?.connectWalletType === 'accountManager') {
            setIsEnterPinCodeModalOpen(true);
          }
        }}
        kitDetail={kitDetail}
        setConnectedWallet={setConnectedWallet}
      />
      <EnterPinCodeModal
        isOpen={isEnterPinCodeModalOpen}
        closeModal={async () => {
          if (password.length === 6) {
            await deployKitWithAccount();
          }
          setPassword('');
          setIsEnterPinCodeModalOpen(false);
        }}
        connectedWallet={connectedWallet}
        setPin={setPassword}
        pin={password}
      />
      <KitContainer title={'Network Info'}>
        {status === TABLE_STATUS.CREATED ? (
          <LabelValue
            label={'Network'}
            value={
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {selectedNetwork?.label && <div>{selectedNetwork?.label}</div>}
                <div
                  className="kit-select-network-button"
                  onClick={() => {
                    setIsOpenSelectNetworkModal(true);
                  }}
                >
                  Select Network
                </div>
              </div>
            }
          />
        ) : (
          <LabelValue label={'Network'} value={kitDetail?.network} />
        )}
      </KitContainer>
      <KitContainer title={'Contract Info'}>
        <LabelValue label={'Contract Title'} value={contractLibrary?.contract?.title} isLoading={getContractInfoLoading} />
        <LabelValue label={'Contract ID'} value={contractInfo?.contract_id} isLoading={getContractInfoLoading} />
        <LabelValue label={'Contract Name'} value={contractInfo?.name} isLoading={getContractInfoLoading} />
        <LabelValue label={'Made By'} value={contractLibrary?.project} isLoading={getContractInfoLoading} />
        <LabelValue label={'Contract Type'} value={contractLibrary?.contract_type_labeling} isLoading={getContractInfoLoading} />
        <LabelValue label={'ERC'} value={contractInfo?.erc_type ? `ERC ${contractInfo?.erc_type}` : ''} isLoading={getContractInfoLoading} />
        <LabelValue label={'Description'} value={contractLibrary?.contract?.description} isLoading={getContractInfoLoading} />
        <LabelValue
          label={'Contract Address'}
          value={
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div>{contractInfo?.contract_address || '-'}</div>
              {contractInfo?.contract_address && (
                <CopyToClipboard text={contractInfo?.contract_address} onCopy={() => toast('Copied Address', { icon: '👏' })}>
                  <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
                </CopyToClipboard>
              )}
            </div>
          }
          isLoading={getContractInfoLoading}
        />
      </KitContainer>
      <KitContainer title={'Version Info'}>
        <LabelValue label={'Contract Version'} value={contractInfo?.origin_version} isLoading={getContractInfoLoading} />
        <LabelValue label={'Version Description'} value={contractLibrary?.version_description} isLoading={getContractInfoLoading} />
      </KitContainer>
      {constructor && constructor.length > 0 && (
        <KitContainer title={'Constructor'}>
          {constructor.map((item, index) => (
            <LabelValue label={`${item.name} (${item.type})`} value={item.description} key={index} />
          ))}
        </KitContainer>
      )}
      {(status === TABLE_STATUS.CREATED || status === TABLE_STATUS.PREPARED) && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          <BasicButton size={ButtonSize.LARGE} onClick={() => onClickDeploy().finally(() => setDeployLoading(false))} disabled={deployLoading}>
            Deploy
          </BasicButton>
        </div>
      )}
    </div>
  );
};

export default ContractManager;
